import React from 'react';

/**
 * Layout-accurate loading skeletons for the full-page editors. They mirror the
 * real structure (card headers, field rows, tab strip) so the page doesn't jump
 * when the detail response arrives.
 *
 * - `CatalogEditorSkeleton` matches the catalog mold (metadata strip + tab strip
 *   + main column of cards + 320px sidebar): Hotels, Activities, Transfers.
 * - `TabbedCardSkeleton` matches the legacy single-card tabbed layout still used
 *   by Meal services and Visas.
 */

const Bar = ({ className = '' }: { className?: string }): React.JSX.Element => (
  <div className={'rounded bg-zinc-200/70 ' + className} />
);

const SkeletonField = (): React.JSX.Element => (
  <div className="space-y-2">
    <Bar className="h-3 w-24" />
    <Bar className="h-9 w-full" />
  </div>
);

/** A card-shaped block: header bar + N field rows. */
export const SkeletonFormCard = ({ fields = 3 }: { fields?: number }): React.JSX.Element => (
  <section className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
    <div className="border-b border-zinc-100 px-5 py-3.5">
      <Bar className="h-4 w-32" />
    </div>
    <div className="space-y-5 px-5 py-5">
      {Array.from({ length: fields }).map((_, i) => (
        <SkeletonField key={i} />
      ))}
    </div>
  </section>
);

/** Metadata strip + tab strip + main/sidebar card grid. */
export const CatalogEditorSkeleton = (): React.JSX.Element => (
  <div className="animate-pulse" aria-hidden>
    <div className="mb-6 flex flex-wrap gap-x-4 gap-y-1">
      <Bar className="h-3 w-28" />
      <Bar className="h-3 w-24" />
      <Bar className="h-3 w-20" />
    </div>
    <div className="mb-6 flex gap-4 border-b border-zinc-100 pb-3">
      <Bar className="h-4 w-16" />
      <Bar className="h-4 w-28" />
    </div>
    <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
      <div className="min-w-0 space-y-6">
        <SkeletonFormCard fields={3} />
        <SkeletonFormCard fields={4} />
        <SkeletonFormCard fields={3} />
      </div>
      <div className="space-y-6">
        <SkeletonFormCard fields={2} />
        <SkeletonFormCard fields={2} />
      </div>
    </div>
  </div>
);

/** Tab strip + a single card of two-column fields (legacy editor layout). */
export const TabbedCardSkeleton = (): React.JSX.Element => (
  <div className="animate-pulse" aria-hidden>
    <section className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
      <div className="flex gap-4 border-b border-zinc-100 px-6 pt-5 pb-3">
        <Bar className="h-4 w-16" />
        <Bar className="h-4 w-20" />
        <Bar className="h-4 w-24" />
      </div>
      <div className="grid gap-5 px-6 py-6 sm:grid-cols-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <SkeletonField key={i} />
        ))}
      </div>
    </section>
  </div>
);
